// react fc component
import { Languages } from '@constants/common.constants'
import useAuth from '@utils/hooks/useAuth'
import { LayoutContext } from '@layouts/MainLayout/LayoutProvider'
import LanguageSwitcher from '@layouts/Shared/Header/LanguageSwitcher'
import { Logout } from '@mui/icons-material'
import { Box, Button, Grid, Stack } from '@mui/material'
import React, { PropsWithChildren, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ROUTE } from '@constants/route.constants'
import { useConfirm } from '@components/Confirm'

interface LayoutProps extends PropsWithChildren {
  leftSide: React.ReactNode
}

const SetupWrapperLayout: React.FC<LayoutProps> = ({ leftSide, children }) => {
  const { changeLang, lang } = useContext(LayoutContext)
  const { signOut } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const confirm = useConfirm()

  const logOut = () => {
    signOut()
      .unwrap()
      .then(() => navigate(ROUTE.ROOT))
      .catch(() => navigate(ROUTE.ROOT))
  }

  const handleSignOut = () => {
    confirm({
      title: t('CONFIRM.logout_title'),
      description: t('CONFIRM.logout_desc'),
    })
      .then(logOut)
      .catch(() => null)
  }

  return (
    <div>
      <Grid
        container
        sx={{
          height: {
            xs: '100%',
            sm: 'calc(100dvh - var(--template-frame-height, 0px))',
          },
          mt: { xs: 4, sm: 4, md: 0 },
        }}
      >
        <Grid
          item
          xs={12}
          sm={4}
          lg={3}
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            backgroundColor: 'hsl(243.75deg 85.33% 97.59%)',
            borderRight: { sm: 'none', md: '1px solid' },
            borderColor: { sm: 'none', md: 'divider' },
            alignItems: 'start',
            pt: 12,
            position: 'relatiive',
            px: 10,
            gap: 4,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              maxWidth: 500,
              gap: 4,
            }}
          >
            {leftSide}
          </Box>
          <Button
            variant="text"
            onClick={() => handleSignOut()}
            sx={{ position: 'absolute', bottom: 0, mb: 4 }}
          >
            {t('USER_MENU.logout')}
          </Button>
        </Grid>
        <Grid
          sm={12}
          md={8}
          lg={9}
          item
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '100%',
            width: '100%',
            backgroundColor: { xs: 'transparent', sm: 'background.default' },
            alignItems: 'start',
            py: { xs: 0, sm: 10 },
            px: { xs: 5, sm: 10 },
            position: 'relative',
            gap: { xs: 4, md: 8 },
          }}
        >
          <Stack
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              left: 0,
              mt: { xs: 0, md: 4 },
              px: 4,
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'space-between',
              mr: 4,
            }}
          >
            <Box>
              <Button
                variant="text"
                sx={{ display: { xs: 'auto', md: 'none' } }}
                onClick={() => handleSignOut()}
              >
                <Logout />
              </Button>
            </Box>
            <Box>
              <LanguageSwitcher
                currentLang={lang}
                data={Languages}
                onSwitch={changeLang}
              />
            </Box>
          </Stack>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              maxWidth: { sm: '100%', md: '100%' },
              gap: 4,
              height: '100%',
              py: { sm: 2, xs: 8 },
            }}
          >
            {children}
          </Box>
        </Grid>
      </Grid>
    </div>
  )
}

export default SetupWrapperLayout
